import { lang } from '@umu/ukit-lang';
/**
 * @description
 */

import { getQueryString } from '@umu/umu-dom';
import { cancelableTimeout } from '@umu/umu-util';
import { fetch } from '@umu/umu-fetch';
import { services } from 'common';
const { Toast } = services;

interface IChargeData {
    [key: string]: any;
}

type TPayCallback = (status?: any) => void;

interface IPayOptions {
    lastStatus?: string;
    timeout?: number;
}

interface IPayArgs {
    chargeData: IChargeData;
    success: TPayCallback;
    fail: TPayCallback;
    options?: IPayOptions;
}

interface IPayResultArgs {
    orderNo: IChargeData;
    success: TPayCallback;
    fail: TPayCallback;
}

interface IPayResultOutput {
    status: boolean;
    data: {
        pay_status: 'success' | 'fail' | 'pay';
    };
}

export interface IOrderData {
    ref_id: number; // 关联id
    ref_type: string; // 关联类型
    pay_channel: string; // 支付类型
    amount: number; //  支付金额(单位分)
    app_type: string; // 应用类型
    order_origin?: string; // 订单来源 market、
}

export interface ITakeOrderOutputData {
    pay_status?: string;
    paypal_html?: string;
    order_key: string;
    charge: IChargeData;
    pay_qr?: string;
}

export interface ITakeOrderOutput {
    status: boolean;
    data: ITakeOrderOutputData;
    errno: number;
    error?: string;
}

/**
 * 轮询支付结果
 * @param orderNo {int} 订单号码
 */
const watchPayResult = cancelableTimeout((args: IPayResultArgs) => {
    const { orderNo, success, fail } = args;
    fetch
        .post('/ajax/pay/status', {
            order_no: orderNo,
        })
        .then((ret: IPayResultOutput) => {
            if (ret.status) {
                if (ret.data.pay_status === 'success' || ret.data.pay_status === 'fail') {
                    watchPayResult.cancel();
                    Toast.hide();
                    success.apply(null, [ret.data]);
                    return;
                } else if (ret.data.pay_status === 'pay') {
                    watchPayResult.cancel();
                    Toast.hide();
                    fail.apply(null, [ret.data]);
                } else {
                    watchPayResult.apply(null, [args]);
                }
            }
        });
}, 2000);

/**
 * 微信公众号支付
 */
function wxPub(args: IPayArgs) {
    const { chargeData, success, fail, options = {} } = args;
    const duration = options.timeout || 10 * 1000;
    // 调用pingpp提供的api，传递charge，完成支付
    // sdk可从 https://github.com/PingPlusPlus/pingpp-js 下载最新dist目录文件，传至cdn更新
    window.pingpp.createPayment(chargeData, (result: string) => {
        // 文档：https://www.pingxx.com/docs/client/web.html
        // 增加后续支付方式时注意特殊处理
        if (result === 'success') {
            // 只有微信公众账号 wx_pub 支付成功的结果会在这里返回，其他的 wap 支付结果都是在 extra 中对应的 URL 跳转。
            // 支付可能是pending的状态，等待服务端的异步回调成功再显示结果
            // 10s超时显示结果
            watchPayResult({
                orderNo: chargeData.order_no,
                success,
                fail,
            });
            Toast.show({
                message: lang('支付中...'),
                duration,
            });
            setTimeout(() => {
                watchPayResult.cancel();
                fail.apply(null, []);
                Toast.hide();
            }, duration);
        } else if (result === 'fail') {
            // charge 不正确或者微信公众账号支付失败时会在此处返回
            fail.apply(null, []);
        } else if (result === 'cancel') {
            // 微信公众账号支付取消支付
            fail.apply(null, []);
        }
    });
}

function wxPubQr(args: IPayArgs) {
    const { chargeData, success, fail, options = {} } = args;
    const duration = options.timeout || 5 * 60 * 1000;
    // 如果支付成功，刷新页面
    watchPayResult({
        orderNo: chargeData.order_no,
        success,
        fail,
    });
    setTimeout(() => {
        watchPayResult.cancel();
        fail.apply(null, [chargeData]);
    }, duration);
}

/**
 * 下订单
 */
function takeOrder(data: IOrderData, callback: (result: ITakeOrderOutput) => void) {
    const orderOrigin = getQueryString('order_origin');
    if (orderOrigin) {
        data.order_origin = orderOrigin;
    }
    fetch.post('/ajax/pay/order', data).then((ret: ITakeOrderOutput) => {
        if (!ret.status) {
            return;
        }
        callback.apply(null, [ret]);
    });
}

/**
 * 通用支付方法
 * @param chargeData {object} 支付参数对象
 * @param success {function} 支付成功方法
 * @param fail {function} 支付失败方法
 * @param options: {[key: string]: any} 参数
 */
function pay(args: IPayArgs) {
    const { chargeData, success, /* fail, */ options = {} } = args;
    const channel = chargeData.channel;

    // 如果需要检查上一次订单的支付状态，且order接口返回的状态为 成功，则不再发起支付
    // 不处理 订单pending 的状态（这种情况下，订单会 pending 5分钟后失效），这种情况比较极端，而且
    // 支付一次以上的情况不可避免
    if (options.lastStatus && options.lastStatus === 'success') {
        success();
        return;
    }
    switch (channel) {
        case 'wx_pub_qr':
            wxPubQr.apply(null, [args]);
            break;
        case 'wx_pub':
            wxPub.apply(null, [args]);
            break;
        default:
            break;
    }
}

export { watchPayResult, pay, takeOrder };
